/* Firestore mirror for Ledger state. Preferences stay small; game caches are
 * chunked because a Firestore document may not exceed 1 MiB. IndexedDB remains
 * the local/offline cache, while this module makes the selected account's
 * history available after signing in on another device.
 *
 * v2 stores games as gzipped JSON (packed off-thread by the ledger worker):
 * chess SAN compresses ~5×, so even a 70k-game full history fits in ~10
 * sub-MiB chunk docs instead of ~180 plain-JSON ones. v1 (plain JSON string
 * chunks) stays readable so pre-upgrade devices' data still hydrates. */
import { Bytes, collection, deleteDoc, doc, getDoc, getDocs, setDoc } from "firebase/firestore";
import { db } from "./firebase.js";
import { packGames, unpackChunks } from "./ledgerWorker.js";
import { prefsForCloud } from "./ledgerPrefs.js";

const VERSION = 2;

const profileRef = (uid) => doc(db, "users", uid, "app", "ledger");
const appStateRef = (uid) => doc(db, "users", uid, "app", "state");
const cacheRef = (uid, key) => doc(db, "users", uid, "ledgerCaches", encodeURIComponent(key));
const chunksRef = (uid, key) => collection(cacheRef(uid, key), "chunks");

/* Chunk generations (see saveRemoteLedgerGames): an upload writes its chunks
 * as `${gen}-0000`…, and the meta it writes last names the live `gen`. The
 * generation carries its own birth time, which is how an upload that died
 * before its meta is told apart from one still in flight. Caches written
 * before generations existed hold bare "0000"… ids — the legacy generation. */
const LEGACY_GENERATION = "";
const STALE_GENERATION_MS = 60 * 60 * 1000;
const newGeneration = () => `g${Date.now()}${Math.random().toString(36).slice(2, 6)}`;
const chunkId = (gen, index) => `${gen}-${String(index).padStart(4, "0")}`;
const generationOf = (id) => (id.includes("-") ? id.slice(0, id.lastIndexOf("-")) : LEGACY_GENERATION);
const generationTime = (gen) => Number(gen.slice(1, 14)) || 0;

/* Set once by accountDeletion.js. Every write in this module is
 * fire-and-forget, and the game-cache one is debounced by nearly two seconds —
 * either could land after the documents were deleted and quietly restore them.
 * A one-way latch: after an account is deleted there is nothing to sync to. */
let halted = false;

/* Throws when the read fails: these prefs name the cache holding the
 * account's games, and a device that read "couldn't ask" as "none" restored
 * nothing and said nothing. */
export async function loadRemoteLedgerPrefs(uid) {
  if (!db) return null;
  const snap = await getDoc(profileRef(uid));
  return snap.exists() ? snap.data() : null;
}

/* `merge: true` merges into the stored `prefs` map field by field, which is
 * what lets prefsForCloud leave an empty username OUT rather than write it
 * over the one another device chose. */
export function saveRemoteLedgerPrefs(uid, prefs) {
  if (!db || !uid || halted) return;
  setDoc(profileRef(uid), { version: VERSION, prefs: prefsForCloud(prefs), updatedAt: Date.now() }, { merge: true })
    .catch((error) => console.warn("ledger sync preference write failed:", error.code || error.message));
}

export async function loadRemoteAppState(uid) {
  if (!db) return null;
  try {
    const snap = await getDoc(appStateRef(uid));
    return snap.exists() ? snap.data() : null;
  } catch (error) {
    console.warn("app state read failed:", error.code || error.message);
    return null;
  }
}

export function saveRemoteAppState(uid, state) {
  if (!db || !uid || halted) return;
  setDoc(appStateRef(uid), { version: VERSION, ...state, updatedAt: Date.now() }, { merge: true })
    .catch((error) => console.warn("app state write failed:", error.code || error.message));
}

let cacheSaveTimer = null;
let pendingCache = null;

export function haltRemoteLedgerWrites() {
  halted = true;
  clearTimeout(cacheSaveTimer);
  cacheSaveTimer = null;
  pendingCache = null;
}

/* The cloud copy only ever grows. This is the LAST line of defence and it is
 * deliberately dumb: if the stored meta already claims more games than we are
 * about to write, don't write. Callers do merge before persisting, but every
 * one of those merges was conditional (on a uid that had resolved, on a remote
 * read that succeeded) and therefore failed open — which is how a stopped
 * mid-stream fetch on a device with an empty local cache replaced a
 * 71,570-game cloud history with 117 games.
 *
 * Refusing is safe: the device keeps its own games and the next sign-in
 * hydrate unions and pushes the correct, larger set. Genuinely replacing a
 * cloud copy with something smaller goes through deleteRemoteLedgerGames
 * (the clear-history affordance), which is explicit and user-initiated. */
export function saveRemoteLedgerGames(uid, key, games, savedAt = Date.now(), complete = false) {
  if (!db || !uid || !key || !games?.length || halted) return;
  pendingCache = { uid, key, games, savedAt, complete };
  clearTimeout(cacheSaveTimer);
  cacheSaveTimer = setTimeout(async () => {
    const job = pendingCache;
    try {
      // A read that THROWS, not the forgiving peek: a failed read that came
      // back as "no meta" would wave every write past the shrink check below.
      const stored = await readRemoteLedgerMeta(job.uid, job.key);
      if ((stored?.gameCount || 0) > job.games.length) {
        // i18n-ignore — a developer console warning.
        console.warn(
          `ledger sync write skipped: cloud holds ${stored.gameCount} games, refusing to overwrite with ${job.games.length}`
        );
        return;
      }
      const { chunks } = await packGames(job.games);
      const ref = cacheRef(job.uid, job.key);
      // A NEW GENERATION of chunk docs every time, never the old ids again.
      // Chunks used to be overwritten in place ("0000"…), so an upload cut
      // short — a tab closed, a phone backgrounded, ~9MB of sequential writes —
      // had already destroyed the previous payload by the time it died: one
      // account's cloud copy became chunks 0–12 of an upload that never
      // finished plus chunk 13 of the one before, under the older meta's
      // promise of 14 chunks. Every chunk present, nothing tore, and it no
      // longer gunzipped, so every new device "restored" nothing and fell back
      // to a full re-download (2026-10-23). The meta, written last, is what
      // makes a generation live; until it lands the old one is untouched.
      //
      // Sequential on purpose: parallel setDocs of ~700KB payloads can get
      // coalesced into one oversized transport RPC and fail wholesale.
      const gen = newGeneration();
      for (let index = 0; index < chunks.length; index += 1) {
        await setDoc(doc(chunksRef(job.uid, job.key), chunkId(gen, index)), {
          data: Bytes.fromUint8Array(new Uint8Array(chunks[index])),
        });
      }
      await setDoc(ref, {
        version: VERSION,
        format: "gzip",
        gen,
        key: job.key,
        savedAt: job.savedAt,
        gameCount: job.games.length,
        chunkCount: chunks.length,
        complete: !!job.complete,
      });
      // Then the generations nothing points at any more: the one this upload
      // replaced, and any upload that died before its meta. Not a concurrent
      // upload still in flight — it is newer than STALE_GENERATION_MS, and if
      // it finishes, its meta simply wins.
      const replaced = stored?.gen || LEGACY_GENERATION;
      const existing = await getDocs(chunksRef(job.uid, job.key));
      await Promise.all(
        existing.docs
          .filter((item) => {
            const owner = generationOf(item.id);
            return owner !== gen && (owner === replaced || Date.now() - generationTime(owner) > STALE_GENERATION_MS);
          })
          .map((item) => deleteDoc(item.ref))
      );
    } catch (error) {
      console.warn("ledger sync game write failed:", error.code || error.message);
    }
  }, 1800);
}

/* Delete one account's cloud history: every chunk, then the meta document.
 * Chunks go first so an interrupted delete leaves a torn cache (which
 * loadRemoteLedgerGames already reads as "no remote cache") rather than a
 * meta doc promising chunks that are gone. Any debounced write still queued
 * for this cache is dropped — otherwise it would land right after the delete
 * and restore what the user just asked to remove. */
export async function deleteRemoteLedgerGames(uid, key) {
  if (!db || !uid || !key) return;
  if (pendingCache && pendingCache.uid === uid && pendingCache.key === key) {
    clearTimeout(cacheSaveTimer);
    pendingCache = null;
  }
  try {
    const chunks = await getDocs(chunksRef(uid, key));
    for (const item of chunks.docs) await deleteDoc(item.ref);
    await deleteDoc(cacheRef(uid, key));
  } catch (error) {
    console.warn("ledger sync delete failed:", error.code || error.message);
    throw error;
  }
}

// Cheap check before paying for the full chunk fetch + gunzip: one small
// document read (no chunks touched) so a caller can decide whether there's
// anything actually newer than what it already has locally.
export async function peekRemoteLedgerMeta(uid, key) {
  try {
    return await readRemoteLedgerMeta(uid, key);
  } catch (error) {
    console.warn("ledger sync meta peek failed:", error.code || error.message);
    return null;
  }
}

/* The same read for callers that must tell "no cloud copy" from "couldn't
 * ask": it throws. */
export async function readRemoteLedgerMeta(uid, key) {
  if (!db || !uid || !key) return null;
  const meta = await getDoc(cacheRef(uid, key));
  return meta.exists() ? meta.data() : null;
}

/* The account's cloud copy of one games cache: null when there is none (or an
 * upload tore it), and a THROW when it exists but can't be had — `kind`
 * "unreachable" when the read failed, "damaged" when the payload won't unpack.
 * Those used to be null too, and "can't read it" then meant "there is
 * nothing", which is how a damaged copy silently sent every new device to
 * re-download from scratch. `onProgress(done, total)` counts chunks. */
export async function loadRemoteLedgerGames(uid, key, { onProgress } = {}) {
  if (!db || !uid || !key) return null;
  const failed = (kind, error) => {
    console.warn(`ledger sync game read failed (${kind}):`, error.code || error.message);
    return Object.assign(new Error(error.message), { kind, cause: error });
  };
  let meta;
  let parts;
  try {
    const snap = await getDoc(cacheRef(uid, key));
    meta = snap.exists() ? snap.data() : null;
    if (!meta?.chunkCount) return null;
    parts = await readChunks(uid, key, meta, onProgress);
  } catch (error) {
    throw failed("unreachable", error);
  }
  // A torn upload (fewer chunks than the meta promises) must read as "no
  // remote cache", not as a truncated-but-plausible game list.
  if (!parts) return null;
  let games;
  try {
    games =
      meta.format === "gzip"
        ? await unpackChunks(parts.map((part) => part.data().data.toUint8Array()))
        : parts.flatMap((part) => JSON.parse(part.data().data || "[]"));
  } catch (error) {
    throw failed("damaged", error);
  }
  // The payload is the truth; the meta is only an index. Report what the
  // chunks actually contain so callers compare against reality — trusting a
  // stale count here is what makes a device believe the cloud is ahead of it
  // forever.
  return games.length ? { ...meta, gameCount: games.length, games } : null;
}

/* The chunk docs `meta` describes, in order, or null if any is missing. A
 * generation's chunks are fetched by id — the collection can also hold a
 * superseded generation, or an upload's that never got its meta. A legacy
 * meta (no `gen`) owns the bare numbered ids. */
async function readChunks(uid, key, meta, onProgress) {
  const total = meta.chunkCount;
  let done = 0;
  onProgress?.(0, total);
  if (meta.gen) {
    const parts = await Promise.all(
      Array.from({ length: total }, async (_, index) => {
        const part = await getDoc(doc(chunksRef(uid, key), chunkId(meta.gen, index)));
        onProgress?.((done += 1), total);
        return part;
      })
    );
    return parts.every((part) => part.exists()) ? parts : null;
  }
  const all = await getDocs(chunksRef(uid, key));
  const ordered = all.docs
    .filter((part) => generationOf(part.id) === LEGACY_GENERATION)
    .sort((a, b) => a.id.localeCompare(b.id))
    .slice(0, total);
  onProgress?.(total, total);
  return ordered.length < total ? null : ordered;
}

